import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "pages/GenericOrder/ui-form/form-field";
import { ModalForm } from "pages/GenericOrder/ui-form/modal-form";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { z } from "zod";
import { toast } from "sonner";
import { useParams } from "react-router-dom";
import { cancelRemitOrder } from "./api";

const schema = z.object({
    reason: z.string().min(1, { message: "Reason is required" }),
    serviceProviderCharge: z
        .union([z.string(), z.number()])
        .refine((val) => val !== "" && val !== null && val !== undefined, {
            message: "Service provider charge is required"
        })
        .transform((val) => Number(val))
        .refine((val) => val >= 0, { message: "Service provider charge must be non-negative" }),
    customerCharge: z
        .union([z.string(), z.number()])
        .refine((val) => val !== "" && val !== null && val !== undefined, {
            message: "Customer charge is required"
        })
        .transform((val) => Number(val))
        .refine((val) => val >= 0, { message: "Customer charge must be non-negative" }),
});

type FieldValues = z.infer<typeof schema>;

interface CancelOrderProps {
    children: React.ReactNode;
    canceller?: "partner" | "customer";
    refetch?: () => void; // Optional refetch function
}

export default function CancelOrder({
    children,
    canceller,
    refetch
}: CancelOrderProps) {
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { id } = useParams();

    const { mutate, isLoading } = useMutation({
        mutationFn: cancelRemitOrder,
        onSuccess: () => {
            toast.success("Cancellation charges set successfully");
            refetch?.();
            setIsOpen(false);
        },
        onError: (error: any) => {
            toast.error(
                `Failed to set cancellation charges: ${error.message || "Unknown error"}`
            );
        },
    });

    const handleSubmit = async (data: FieldValues) => {
        let charges = {};
        if (canceller === "partner") {
            charges = {
                service_provider_charges: data.serviceProviderCharge || 0,
                l2_charges: data.customerCharge || 0,
            }
        }
        else {
            charges = {
                service_provider_charges: data.serviceProviderCharge || 0,
                customer_charges: data.customerCharge || 0,
            }
        }
        const payload: any = {
            order_id: id,
            cancellation_reason: data.reason,
            cancellation_charges: charges
        };
        mutate(payload);
    };

    return (
        <>
            <Button variant="default" onClick={() => setIsOpen(true)}>
                {children}
            </Button>

            <ModalForm<FieldValues>
                title="Cancellation Charges"
                isOpen={isOpen}
                onClose={() => {
                    setIsOpen(false);
                }}
                onSubmit={handleSubmit}
                submitButtonText="Submit"
                schema={schema}
                defaultValues={undefined}
                isLoading={isLoading}
                skipDirtyCheck={true}
                className="max-w-2xl"
            >
                {(form) => {
                    const { serviceProviderCharge = 0, customerCharge = 0 } =
                        form.watch();
                    const total = Number(serviceProviderCharge) + Number(customerCharge);

                    console.log("Form values:", form.formState.errors);

                    return (
                        <div className="space-y-6 w-full">
                            {/* Header with Total Charges */}
                            <div className="flex justify-between items-start">
                                <div className="bg-gray-800 text-white-A700 w-full px-6 py-3 rounded-lg">
                                    <div className="flex items-center gap-4">
                                        <span className="text-white">Total Charges</span>
                                        <div className="bg-gray-700 px-3 py-1 rounded text-sm">
                                            INR
                                        </div>
                                        <span className="text-xl font-semibold">
                                            {total || 0}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {/* Reason Input Field */}
                            <FormField
                                form={form}
                                name="reason"
                                label="Cancellation Reason"
                                render={({ field }) => (
                                    <Input
                                        {...field}
                                        placeholder="Enter cancellation reason"
                                        className="h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                                    />
                                )}
                            />

                            {/* Charges Input Fields */}
                            <div className="grid grid-cols-2 sm:grid-cols-1 gap-6">
                                <FormField
                                    form={form}
                                    label="Service Provider Charges"
                                    name="serviceProviderCharge"
                                    render={({ field }) => (
                                        <div className="relative">
                                            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-blue-100 text-blue-600 px-2 py-1 rounded text-sm font-medium">
                                                INR
                                            </div>
                                            <Input
                                                {...field}
                                                type="number"
                                                placeholder="Enter Service Partner Rates"
                                                className="pl-16 h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                    )}
                                />
                                <FormField
                                    form={form}
                                    name="customerCharge"
                                    label={
                                        canceller === 'partner' ? 'Channel Partner Charges' : 'Customer Charges'
                                    }
                                    render={({ field }) => (
                                        <div className="relative">
                                            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 bg-blue-100 text-blue-600 px-2 py-1 rounded text-sm font-medium">
                                                INR
                                            </div>
                                            <Input
                                                {...field}
                                                type="number"
                                                placeholder="Enter cancellation charges"
                                                className="pl-16 h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                                            />
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    );
                }}
            </ModalForm>
        </>
    );
}